import { useMutation, useQueryClient } from '@tanstack/react-query';

import { FulfillmentActionItemCode } from '@ekhodealer/ekho-common';

import type { MarkReceivedAtFulfillmentCenterResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

import { updateProgressCache } from './progressCacheUtils';


/**
 * API function to mark vehicle as received at fulfillment center
 * @param servicesProviderId - The services provider ID
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const markReceivedAtFulfillmentCenter = async (
	servicesProviderId: string,
	orderId: string,
): Promise<MarkReceivedAtFulfillmentCenterResponse> => {
	const response = await apiClient.post<MarkReceivedAtFulfillmentCenterResponse>(
		ordersEndpoints.markReceivedAtFulfillmentCenter(servicesProviderId, orderId),
		{}, // Empty body as per backend schema
	);
	return response.data;
};

/**
 * Hook to mark vehicle as received at fulfillment center
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useMarkReceivedAtFulfillmentCenter = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			if (!orderId || !servicesProviderId) {
				throw new Error(
					'Unable to mark as received at fulfillment center without an order ID.',
				);
			}

			return markReceivedAtFulfillmentCenter(servicesProviderId, orderId);
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, FulfillmentActionItemCode.MARK_RECEIVED_AT_FULFILLMENT_CENTER);
		},
	});
};
